/** @format */

/**
 * HIPAA Audit Log Types
 * Based on 45 C.F.R. § 164.312(b) requirements
 */

export enum AuditActionType {
  // Authentication Actions
  LOGIN = 'LOGIN',
  LOGOUT = 'LOGOUT',
  LOGIN_FAILED = 'LOGIN_FAILED',
  PASSWORD_RESET = 'PASSWORD_RESET',
  PASSWORD_CHANGE = 'PASSWORD_CHANGE',
  OTP_SEND = 'OTP_SEND',
  OTP_VERIFY = 'OTP_VERIFY',
  SESSION_TIMEOUT = 'SESSION_TIMEOUT',

  // CRUD Operations
  CREATE = 'CREATE',
  READ = 'READ',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',

  // Access Actions
  VIEW = 'VIEW',
  DOWNLOAD = 'DOWNLOAD',
  UPLOAD = 'UPLOAD',
  SEARCH = 'SEARCH',
  FILTER = 'FILTER',
  SAVE = 'SAVE',
  // Removed action types (commented out):
  // ACCESS = 'ACCESS', // Removed - use VIEW instead
  // EXPORT = 'EXPORT', // Removed - use DOWNLOAD instead
  // PRINT = 'PRINT', // Removed

  // Administrative Actions
  USER_INVITE = 'USER_INVITE',
  USER_ROLE_CHANGE = 'USER_ROLE_CHANGE',
  USER_DEACTIVATE = 'USER_DEACTIVATE',
  PERMISSION_GRANT = 'PERMISSION_GRANT',
  PERMISSION_REVOKE = 'PERMISSION_REVOKE',
  
  // Removed Document Operations (commented out):
  // DOCUMENT_UPLOAD = 'DOCUMENT_UPLOAD', // Removed - use UPLOAD instead
  // DOCUMENT_DOWNLOAD = 'DOCUMENT_DOWNLOAD', // Removed - use DOWNLOAD instead
  // DOCUMENT_VIEW = 'DOCUMENT_VIEW', // Removed - use VIEW instead
  // DOCUMENT_DELETE = 'DOCUMENT_DELETE', // Removed - use DELETE instead
  // DOCUMENT_SHARE = 'DOCUMENT_SHARE', // Removed - use PERMISSION_GRANT instead

  // Audit Log Actions
  AUDIT_LOG_ACCESS = 'AUDIT_LOG_ACCESS',
  AUDIT_LOG_EXPORT = 'AUDIT_LOG_EXPORT',

  // AffableFLOW Integration Actions
  CONNECT_WITH_AFFABLEFLOW = 'CONNECT_WITH_AFFABLEFLOW',
  UPLOAD_FROM_AFFABLE = 'UPLOAD_FROM_AFFABLE',
}

export enum ResourceType {
  // 1. Facility Analysis
  FACILITY = 'FACILITY',
  FACILITY_DETAILS = 'FACILITY_DETAILS',
  FACILITY_LIST = 'FACILITY_LIST',
  FACILITY_ANALYSIS = 'FACILITY_ANALYSIS',

  // 2. Potential Penalties
  PENALTY_CALCULATION = 'PENALTY_CALCULATION',
  POTENTIAL_PENALTIES = 'POTENTIAL_PENALTIES',

  // 3. Exclusion / Sanction Check
  EXCLUSION_SANCTION_CHECK = 'EXCLUSION_SANCTION_CHECK',

  // 4. Continuous Compliance
  COMPLIANCE_DATA = 'COMPLIANCE_DATA',
  COMPLIANCE_REPORT = 'COMPLIANCE_REPORT',
  REGULATORY_REQUIREMENT = 'REGULATORY_REQUIREMENT',
  AUDIT_CHECKLIST = 'AUDIT_CHECKLIST',
  CONTINUOUS_COMPLIANCE = 'CONTINUOUS_COMPLIANCE',

  // 5. Risk Analysis
  RISK_ANALYSIS = 'RISK_ANALYSIS',

  // 6. Mock Survey
  MOCK_SURVEY = 'MOCK_SURVEY',

  // 7. Survey Viewer from Mock survey screen
  SURVEY_VIEWER = 'SURVEY_VIEWER',

  // 8. User Management
  USER_PROFILE = 'USER_PROFILE',
  USER_LIST = 'USER_LIST',
  USER_INVITATION = 'USER_INVITATION',
  USER_TYPE = 'USER_TYPE',
  USER_SUBTYPE = 'USER_SUBTYPE',
  USER_MANAGEMENT = 'USER_MANAGEMENT',

  // 9. Billings
  BILLING_INFO = 'BILLING_INFO',
  PAYMENT = 'PAYMENT',
  PLAN = 'PLAN',
  BILLINGS = 'BILLINGS',

  // 10. Audit Logs
  AUDIT_LOG = 'AUDIT_LOG',
  AUDIT_REPORT = 'AUDIT_REPORT',
  AUDIT_LOGS = 'AUDIT_LOGS',

  // 11. Authentication / Session
  USER = 'USER',
  SESSION = 'SESSION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHENTICATION_SESSION = 'AUTHENTICATION_SESSION',

  // Discovery
  DISCOVERY_CONTENT = 'DISCOVERY_CONTENT',

  // Facility network link / unlink
  NETWORK = 'NETWORK',

  // Commented out - not in the required resource types list:
  // Documents (Potentially ePHI)
  // DOCUMENT = 'DOCUMENT',
  // OPERATIONAL_DOCUMENT = 'OPERATIONAL_DOCUMENT',

  // ARTICLE = 'ARTICLE',
}

export enum AuditActionStatus {
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
  PARTIAL = 'PARTIAL',
  DENIED = 'DENIED',
}

export enum RiskLevel {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL',
}

export interface DeviceInfo {
  userAgent: string;
  browser: string;
  browserVersion: string;
  os: string;
  osVersion: string;
  deviceType: 'mobile' | 'tablet' | 'desktop';
  screenResolution: string;
  language: string;
}

export interface LocationData {
  ipAddress: string;
  city?: string;
  region?: string;
  country?: string;
  countryCode?: string;
  latitude?: number;
  longitude?: number;
  timezone?: string;
  org?: string;
}

export interface AuditLogEntry {
  id?: string; // UUID, generated by backend
  timestamp: string; // ISO 8601 format

  // User Information
  userId?: number;
  userEmail?: string;
  userName?: string;

  // Action Details
  actionType: AuditActionType;
  actionStatus: AuditActionStatus;
  actionDetails?: Record<string, any>;

  // Resource Information
  resourceType: ResourceType;
  resourceId?: string | number;
  resourceName?: string;

  // Before/After Values (for UPDATE operations)
  beforeValue?: Record<string, any>;
  afterValue?: Record<string, any>;

  // Technical Details
  sessionId?: string;
  deviceInfo?: DeviceInfo;
  locationData?: LocationData;
  authenticationMethod?: 'MSAL' | 'Google' | 'Email' | 'OTP';

  // Context
  accessReason?: string; // User-provided reason for access
  requestPath?: string; // API endpoint called
  requestMethod?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  responseStatusCode?: number;
  responseTime?: number; // milliseconds

  // Security & Compliance
  isAdminAction?: boolean;
  riskLevel?: RiskLevel;
  complianceFlags?: string[];

  // Application Context
  applicationVersion?: string;

  // Retention
  retentionUntil?: string; // ISO 8601, calculated: timestamp + 6 years
}

export interface AuditLogSearchParams {
  startDate?: string;
  endDate?: string;
  userId?: number;
  userEmail?: string;
  actionType?: AuditActionType | AuditActionType[];
  resourceType?: ResourceType | ResourceType[];
  actionStatus?: AuditActionStatus;
  riskLevel?: RiskLevel;
  searchTerm?: string;
  facilityId?: number;
  page?: number;
  pageSize?: number;
  sortBy?: 'timestamp' | 'actionType' | 'resourceType' | 'riskLevel';
  sortOrder?: 'asc' | 'desc';
}

export interface AuditLogResponse {
  success: boolean;
  message?: string;
  logs: AuditLogEntry[];
  total: number;
  page: number;
  pageSize: number;
}

export interface AuditLogExportParams {
  format: 'csv' | 'json' | 'pdf';
  startDate?: string;
  endDate?: string;
  filters?: AuditLogSearchParams;
}

export interface AuditSummaryStats {
  totalLogs: number;
  successfulActions: number;
  failedActions: number;
  uniqueUsers: number;
  highRiskActions: number;
  topActions: Array<{ action: AuditActionType; count: number }>;
  topResources: Array<{ resource: ResourceType; count: number }>;
  recentActivity: AuditLogEntry[];
}

export interface UserAuditTrail {
  userId: number;
  userEmail: string;
  userName: string;
  logs: AuditLogEntry[];
  summary: {
    totalActions: number;
    lastActivity: string;
    frequentActions: Array<{ action: AuditActionType; count: number }>;
  };
}
